
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Activity } from '@/types';

export const useActivities = (projectId?: string) => {
  return useQuery({
    queryKey: ['activities', projectId],
    queryFn: async () => {
      let query = supabase
        .from('activities')
        .select(`
          *,
          comments (*)
        `)
        .order('due_date', { ascending: true });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data.map(activity => ({
        id: activity.id,
        name: activity.name,
        description: activity.description || '',
        status: activity.status || 'not-started',
        assignedTo: activity.assigned_to || '',
        dueDate: new Date(activity.due_date),
        dateCompleted: activity.date_completed ? new Date(activity.date_completed) : undefined,
        stage: activity.stage,
        isRequired: activity.is_required || true,
        comments: activity.comments.map(comment => ({
          id: comment.id,
          text: comment.text,
          author: comment.author,
          dateCreated: new Date(comment.date_created)
        }))
      })) as Activity[];
    },
    enabled: !!projectId
  });
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (activityData: Partial<Activity> & { projectId: string }) => {
      const { data, error } = await supabase
        .from('activities')
        .insert({
          project_id: activityData.projectId,
          name: activityData.name!,
          description: activityData.description,
          status: activityData.status || 'not-started',
          assigned_to: activityData.assignedTo,
          due_date: activityData.dueDate!.toISOString().split('T')[0],
          stage: activityData.stage!,
          is_required: activityData.isRequired
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activities', variables.projectId] });
    }
  });
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, projectId, ...updates }: Partial<Activity> & { id: string; projectId: string }) => {
      const { data, error } = await supabase
        .from('activities')
        .update({
          name: updates.name,
          description: updates.description,
          status: updates.status,
          assigned_to: updates.assignedTo,
          due_date: updates.dueDate?.toISOString().split('T')[0],
          date_completed: updates.dateCompleted?.toISOString().split('T')[0],
          stage: updates.stage,
          is_required: updates.isRequired
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activities', variables.projectId] });
    }
  });
};
